// steps/tabs/meter-readings/MeterReadingTab.tsx
import {
  useDraftDetails,
  useMeterReadings,
} from "@features/bx/BulkBillGeneration/hooks";
import { Button } from "@shared/ui/button";
import { Tabs } from "@shared/ui/tabs";
import { PAGE_SIZE } from "@shared/utils/constants";
import { normalizeDate } from "@shared/utils/dateUtils";
import dayjs from "dayjs";
import { Calendar, Info } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import ActualReadingsSubTab from "./SubTabs/ActualReadings";
import AllReadingsSubTab from "./SubTabs/AllReading";
import EstimatedReadingsSubTab from "./SubTabs/EstimatedReadings";
import { HighConsumptionTable } from "./SubTabs/HighConsumption";
import { LowConsumptionTable } from "./SubTabs/LowConsumptionTable";
import { MissingReadings } from "./SubTabs/MissingReadings";
import { PendingValidationTable } from "./SubTabs/PendingValidation";

interface MeterReadingTabProps {
  data?: any;
  summaryData: any;
  onInvoiceEndChange?: (date: string | null) => void;
}
interface MeterReadingFiltersType {
  veeStatus: string;
  status: string;
  meterType: string;
  service: string;
  page?: number;
  limit?: number;
  search?: string;
}
const MeterReadingTab: React.FC<MeterReadingTabProps> = ({
  data,
  summaryData,
  onInvoiceEndChange,
}) => {
  const { id, cycleId, action } = useParams();
  const { data: draftDetails } = useDraftDetails(id);
  const [currentPage, setCurrentPage] = useState(1);

  const [filters, setFilters] = useState<MeterReadingFiltersType>({
    veeStatus: "all",
    status: "all",
    meterType: "all",
    service: "all",
    page: currentPage,
    limit: PAGE_SIZE,
  });

  const handleExtendPeriod = () => {
    if (!invoiceEnd || isExtended) return;

    const extendedDate = dayjs(invoiceEnd).add(5, "day").format("YYYY-MM-DD");

    setInvoiceEnd(extendedDate);
    setIsExtended(true);

    onInvoiceEndChange?.(extendedDate);
  };

  const [invoiceStart, setInvoiceStart] = useState<string | null>(null);
  const [invoiceEnd, setInvoiceEnd] = useState<string | null>(null);
  const [originalInvoiceEnd, setOriginalInvoiceEnd] = useState<string | null>(
    null,
  );
  const [isExtended, setIsExtended] = React.useState(false);
  const formatFiltersForHook = (
    filters: MeterReadingFiltersType,
  ): Record<string, any> => {
    const hookFilters: Record<string, any> = {
      page: currentPage,
      limit: PAGE_SIZE,
    };

    // Add search term
    if (filters.search && filters.search.trim()) {
      hookFilters.search = filters.search.trim();
    }

    // Add VEE status filter
    if (filters.veeStatus && filters.veeStatus !== "all") {
      hookFilters.veeStatus = filters.veeStatus;
    }

    // Add status filter
    if (filters.status && filters.status !== "all") {
      hookFilters.status = filters.status;
    }

    // Add meter type filter
    if (filters.meterType && filters.meterType !== "all") {
      hookFilters.meterType = filters.meterType;
    }

    // Add service filter
    if (filters.service && filters.service !== "all") {
      hookFilters.service = filters.service;
    }

    return hookFilters;
  };
  const { data: meterReadings, isLoading } = useMeterReadings(
    cycleId,
    id,
    formatFiltersForHook(filters),
  );

  // Subtabs configuration
  const meterReadingSubTabs = {
    all: {
      label: "All Readings",
      count: summaryData?.summaryCards?.totalMeters,
      component: (
        <AllReadingsSubTab
          meterReadings={meterReadings}
          filters={filters}
          setFilters={setFilters}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
        />
      ),
    },
    actual: {
      label: "Actual",
      count: summaryData?.summaryCards?.actualBills,
      component: <ActualReadingsSubTab data={meterReadings} />,
    },
    estimated: {
      label: "Estimated",
      count: summaryData?.summaryCards?.estimatedBills,
      component: <EstimatedReadingsSubTab data={data} />,
    },

    missingReadings: {
      label: "Missing Readings",
      count: summaryData?.summaryCards?.missingReadings,
      component: <MissingReadings />,
    },
    pendingValidation: {
      label: "Pending Validation",
      count: summaryData?.summaryCards?.pendingValidation,
      component: <PendingValidationTable />,
    },
    highConsumption: {
      label: "High Consumption",
      count: summaryData?.summaryCards?.highConsumption,
      component: <HighConsumptionTable />,
    },
    lowConsumption: {
      label: "Low Consumption",
      count: summaryData?.summaryCards?.lowConsumption,
      component: <LowConsumptionTable />,
    },
  };

  useEffect(() => {
    const start = draftDetails?.billDraftData?.step_1Config?.invoiceStartDate;
    const end = draftDetails?.billDraftData?.step_1Config?.invoiceEndDate;

    try {
      if (start) setInvoiceStart(normalizeDate(start));
      if (end) {
        const normalizedEnd = normalizeDate(end);
        setInvoiceEnd(normalizedEnd);
        setOriginalInvoiceEnd(normalizedEnd);

        onInvoiceEndChange?.(normalizedEnd); // 👈 notify parent
      }
    } catch (e) {
      console.error("Date normalization failed", e);
    }
  }, [draftDetails]);

  const handleUndoExtension = () => {
    if (!originalInvoiceEnd) return;

    setInvoiceEnd(originalInvoiceEnd);
    setIsExtended(false);

    onInvoiceEndChange?.(originalInvoiceEnd); // 👈 notify parent
  };

  return (
    <div className="space-y-6">
      {/* Meter Reading Period Section */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center gap-2 mb-2">
          <Calendar className="w-5 h-5 text-blue-600" />
          <h3 className="font-semibold text-lg">Readings Period</h3>
        </div>
        <p className="text-sm text-gray-600 mb-4">
          The system will look for the latest reading within this period
          to calculate the bill.
        </p>

        <div className="bg-gray-50 rounded-lg border border-gray-200 p-4 mb-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-600 mb-1">Billing Period</p>

              <p className="text-base font-semibold">
                {invoiceStart} to {invoiceEnd}
                {isExtended && (
                  <span className="ml-2 text-xs text-blue-600 font-medium">
                    (+5 days extension)
                  </span>
                )}
              </p>
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleExtendPeriod}
                disabled={isExtended}
              >
                <Calendar className="w-4 h-4 mr-2" />
                Extend by 5 Days
              </Button>

              {isExtended && (
                <Button
                  variant="outline"
                  onClick={handleUndoExtension}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </Button>
              )}
            </div>
          </div>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start gap-2">
          <Info className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-blue-900">
            The system will look for the latest reading within this period
            to calculate the bill. Readings outside this range will not be
            considered for the current billing cycle.
          </p>
        </div>
        {isExtended && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-start gap-2 mt-4">
            <Info className="w-4 h-4 text-green-600 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-green-800">
                Extended Period Applied
              </p>
              <p className="text-sm text-green-700">
                New end date: {invoiceEnd}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Subtabs */}
      <Tabs
        tabsListClassName="grid grid-cols-7 w-full bg-gradient-to-r from-blue-100/50 to-indigo-100/50 shadow-sm"
        tabComponents={meterReadingSubTabs}
        disableUrlSync={true}
      />
    </div>
  );
};

export default MeterReadingTab;
